import { useCallback, useEffect, useRef, useState } from 'react';
import pdcService from '../services/pdcService';

/**
 * Session Plan de Com personnelle + historique des chiffrages.
 */
export function usePlanDeComSession() {
  const [data, setData] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveGenRef = useRef(0);

  const dossierId = data?.dossier?.id ?? null;

  const loadDossiers = useCallback(async (filters = {}) => {
    try {
      const result = await pdcService.listDossiers({
        limit: 100,
        scope: 'mine',
        ...filters,
      });
      setDossiers(result.dossiers ?? []);
      return result.dossiers ?? [];
    } catch {
      setDossiers([]);
      return [];
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const full = await pdcService.getSession();
      setData(full);
      await loadDossiers();
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [loadDossiers]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFull = useCallback(async (full) => {
    setData(full);
    await loadDossiers();
    return full;
  }, [loadDossiers]);

  const save = useCallback(async (payload) => {
    if (!dossierId) return null;
    const gen = ++saveGenRef.current;
    setSaving(true);
    setError(null);
    try {
      const full = await pdcService.updateDossier(dossierId, payload);
      if (gen === saveGenRef.current) {
        setData(full);
        loadDossiers();
      }
      return full;
    } catch (err) {
      if (gen === saveGenRef.current) {
        setError(err.message);
      }
      throw err;
    } finally {
      if (gen === saveGenRef.current) {
        setSaving(false);
      }
    }
  }, [dossierId, loadDossiers]);

  const reset = useCallback(async () => {
    if (!dossierId) return null;
    setSaving(true);
    setError(null);
    try {
      const full = await pdcService.resetDossier(dossierId);
      setData(full);
      return full;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [dossierId]);

  const startNew = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      return await applyFull(await pdcService.startNewSession());
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [applyFull]);

  const openDossier = useCallback(async (id) => {
    setSaving(true);
    setError(null);
    try {
      return await applyFull(await pdcService.openDossier(id));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [applyFull]);

  const duplicateDossier = useCallback(async (id) => {
    setSaving(true);
    setError(null);
    try {
      return await applyFull(await pdcService.duplicateDossier(id));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [applyFull]);

  const deleteDossier = useCallback(async (id) => {
    setSaving(true);
    setError(null);
    try {
      return await applyFull(await pdcService.deleteDossier(id));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [applyFull]);

  const renameDossier = useCallback(async (id, titre) => {
    if (!id) return null;
    setSaving(true);
    setError(null);
    try {
      const full = await pdcService.updateDossier(id, { titre });
      if (id === dossierId) {
        setData(full);
      }
      await loadDossiers();
      return full;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [dossierId, loadDossiers]);

  return {
    data,
    dossiers,
    loading,
    error,
    saving,
    dossierId,
    reload: load,
    save,
    reset,
    startNew,
    openDossier,
    duplicateDossier,
    deleteDossier,
    renameDossier,
    loadDossiers,
  };
}

export default usePlanDeComSession;
